import { useEffect, useState } from 'react';
import { isHexColor, PLATFORM_BRAND, themeCss, type BrandInput, type BrandingDTO } from '@zuri/shared';

/**
 * Motor de tema: instala os tokens (packages/shared/src/theme.ts) como variáveis CSS para a marca
 * ativa — identidade neutra da plataforma ou a marca da empresa — e aplica as preferências do usuário
 * (tema claro/escuro/sistema e redução de transparência).
 */

export type ThemePref = 'system' | 'light' | 'dark';
export type GlassPref = 'auto' | 'off';

const STYLE_ID = 'zuri-theme';
let installedKey = '';

export function brandFromBranding(b: BrandingDTO | null | undefined): BrandInput {
  if (!b || !isHexColor(b.primaryColor)) return PLATFORM_BRAND;
  return { primary: b.primaryColor, accent: b.accentColor, highlight: b.highlightColor };
}

/** Instala (ou troca) os tokens. Chamado antes do primeiro render e a cada troca de contexto. */
export function installTheme(brand: BrandInput = PLATFORM_BRAND) {
  const key = `${brand.primary}|${brand.accent}|${brand.highlight}`;
  if (key === installedKey) return;
  installedKey = key;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = themeCss(brand);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', document.documentElement.dataset.theme === 'dark' ? '#0C1312' : '#F5F7F6');
}

/** Ícone da aba: símbolo da empresa quando existir; senão, o da plataforma. */
export function setFavicon(href: string | null) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  const next = href ?? '/favicon.svg';
  if (link.getAttribute('href') !== next) {
    link.setAttribute('href', next);
    link.setAttribute('type', href ? 'image/svg+xml' : 'image/svg+xml');
  }
}

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function applyMode(pref: ThemePref) {
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(() => readPref('zuri-theme', ['system', 'light', 'dark'] as const, 'system'));
  useEffect(() => {
    applyMode(pref);
    try {
      localStorage.setItem('zuri-theme', pref);
    } catch {
      /* armazenamento indisponível */
    }
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyMode('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);
  return { pref, setPref };
}

/** Preferência local de transparência (além de prefers-reduced-transparency do sistema). */
export function useGlass() {
  const [glass, setGlass] = useState<GlassPref>(() => readPref('zuri-glass', ['auto', 'off'] as const, 'auto'));
  useEffect(() => {
    document.documentElement.dataset.glass = glass;
    try {
      localStorage.setItem('zuri-glass', glass);
    } catch {
      /* armazenamento indisponível */
    }
  }, [glass]);
  return { glass, setGlass };
}
